import { ApiResponse, Category, Subtask, SubtaskFormData, Task, TaskComment, TaskFormData, TaskStats, TimerSession, TimerStats, User } from '../types';
import api from './apiClient';

export const tasksAPI = {
  getTasks: async (params?: {
    category?: string;
    project?: string;
    completed?: boolean;
    search?: string;
    status?: string;
    archived?: 0 | 1 | 'all';
    limit?: number;
    offset?: number;
  }): Promise<ApiResponse<{ tasks: Task[] }>> => {
    const response = await api.get('/tasks', { params });
    return response.data;
  },

  getTask: async (id: number): Promise<ApiResponse<{ task: Task }>> => {
    const response = await api.get(`/tasks/${id}`);
    return response.data;
  },

  createTask: async (data: TaskFormData): Promise<ApiResponse<{ task: Task }>> => {
    const response = await api.post('/tasks', data);
    return response.data;
  },

  updateTask: async (id: number, data: Partial<TaskFormData>): Promise<ApiResponse<{ task: Task }>> => {
    const response = await api.put(`/tasks/${id}`, data);
    return response.data;
  },

  updateTaskStatus: async (id: number, status: Task['status']): Promise<ApiResponse<{ task: Task }>> => {
    const response = await api.put(`/tasks/${id}/status`, { status });
    return response.data;
  },

  bulkUpdateTasks: async (data: {
    taskIds: number[];
    assigneeId?: number | null;
    dueDate?: string | null;
    status?: Task['status'];
  }): Promise<ApiResponse<{ updatedIds: number[]; skippedIds: number[] }>> => {
    const response = await api.post('/tasks/bulk-update', data);
    return response.data;
  },

  addComment: async (
    id: number,
    data: { text: string; type?: string; isVisibleToClient?: boolean }
  ): Promise<ApiResponse<{ comment: TaskComment }>> => {
    const response = await api.post(`/tasks/${id}/comments`, data);
    return response.data;
  },

  deleteTask: async (id: number): Promise<ApiResponse> => {
    const response = await api.delete(`/tasks/${id}`);
    return response.data;
  },

  restoreTask: async (id: number): Promise<ApiResponse<{ task: Task }>> => {
    const response = await api.post(`/tasks/${id}/restore`);
    return response.data;
  },

  getStats: async (): Promise<ApiResponse<TaskStats>> => {
    const response = await api.get('/tasks/stats/summary');
    return response.data;
  },
};

// Categories API
export const categoriesAPI = {
  getCategories: async (): Promise<ApiResponse<{ categories: Category[] }>> => {
    const response = await api.get('/categories');
    return response.data;
  },

  createCategory: async (data: { name: string; color?: string }): Promise<ApiResponse<{ category: Category }>> => {
    const response = await api.post('/categories', data);
    return response.data;
  },

  updateCategory: async (id: number, data: { name?: string; color?: string }): Promise<ApiResponse<{ category: Category }>> => {
    const response = await api.put(`/categories/${id}`, data);
    return response.data;
  },

  deleteCategory: async (id: number): Promise<ApiResponse> => {
    const response = await api.delete(`/categories/${id}`);
    return response.data;
  },
};

export const usersAPI = {
  getAvailableUsers: async (): Promise<ApiResponse<{ users: User[] }>> => {
    const response = await api.get('/users/available');
    return response.data;
  },
};

export const subtasksAPI = {
  getSubtasks: async (taskId: number): Promise<ApiResponse<{ subtasks: Subtask[] }>> => {
    const response = await api.get(`/subtasks/task/${taskId}`);
    return response.data;
  },

  getSubtask: async (id: number): Promise<ApiResponse<{ subtask: Subtask }>> => {
    const response = await api.get(`/subtasks/${id}`);
    return response.data;
  },

  createSubtask: async (data: SubtaskFormData): Promise<ApiResponse<{ subtask: Subtask }>> => {
    // Mapowanie nazw pól z camelCase na snake_case dla kompatybilności z backendem
    const mappedData = {
      task_id: data.taskId,
      title: data.title,
      description: data.description,
      completed: data.completed,
      start_date: data.startDate,
      due_date: data.endDate, // endDate mapuje na due_date
      estimated_time: data.estimatedTime
    };
    
    const response = await api.post('/subtasks', mappedData);
    return response.data;
  },

  updateSubtask: async (id: number, data: Partial<SubtaskFormData>): Promise<ApiResponse<{ subtask: Subtask }>> => {
    // Mapowanie nazw pól z camelCase na snake_case dla kompatybilności z backendem
    const mappedData: any = {};
    
    if (data.taskId !== undefined) mappedData.task_id = data.taskId;
    if (data.title !== undefined) mappedData.title = data.title;
    if (data.description !== undefined) mappedData.description = data.description;
    if (data.completed !== undefined) mappedData.completed = data.completed;
    if (data.startDate !== undefined) mappedData.start_date = data.startDate;
    if (data.endDate !== undefined) mappedData.due_date = data.endDate;
    if (data.estimatedTime !== undefined) mappedData.estimated_time = data.estimatedTime;
    
    const response = await api.put(`/subtasks/${id}`, mappedData);
    return response.data;
  },

  deleteSubtask: async (id: number): Promise<ApiResponse> => {
    const response = await api.delete(`/subtasks/${id}`);
    return response.data;
  },

  reorderSubtasks: async (taskId: number, subtaskIds: number[]): Promise<ApiResponse> => {
    const response = await api.put(`/subtasks/reorder/${taskId}`, { subtaskIds });
    return response.data;
  },
};

// Timers API
export const timersAPI = {
  getActiveSessions: async (): Promise<ApiResponse<{ activeSessions: TimerSession[] }>> => {
    const response = await api.get('/timers/active');
    return response.data;
  },

  getSessions: async (filter?: {
    period?: 'today' | 'week' | 'month';
    taskId?: string;
    projectId?: string;
    page?: number;
    limit?: number;
  }): Promise<ApiResponse<{ sessions: TimerSession[] }>> => {
    const response = await api.get('/timers/sessions', { params: filter });
    return response.data;
  },

  getHistory: async (params?: {
    page?: number;
    limit?: number;
    task_id?: number;
    subtask_id?: number;
    inbox_item_id?: number;
    date_from?: string;
    date_to?: string;
  }): Promise<ApiResponse<{ 
    sessions: TimerSession[]; 
    pagination: { page: number; limit: number; total: number; pages: number }
  }>> => {
    const response = await api.get('/timers/history', { params });
    return response.data;
  },

  startTimer: async (data: {
    task_id?: number;
    subtask_id?: number;
    inbox_item_id?: number;
    description?: string;
  }): Promise<ApiResponse<{ session: TimerSession }>> => {
    const response = await api.post('/timers/start', data);
    return response.data;
  },

  stopTimer: async (sessionId: number): Promise<ApiResponse<{ session: TimerSession }>> => {
    const response = await api.post(`/timers/stop/${sessionId}`);
    return response.data;
  },

  stopAllTimers: async (): Promise<ApiResponse<{ sessions: TimerSession[] }>> => {
    const response = await api.post('/timers/stop-all');
    return response.data;
  },

  getStats: async (period?: 'today' | 'week' | 'month'): Promise<ApiResponse<{ stats: TimerStats }>> => {
    const response = await api.get('/timers/stats', { params: { period } });
    return response.data;
  },

  exportSessions: async (filter?: {
    period?: 'today' | 'week' | 'month';
    taskId?: string;
    projectId?: string;
  }): Promise<ApiResponse<{ data: string }>> => {
    const response = await api.get('/timers/export', { 
      params: filter,
      headers: { 'Accept': 'text/csv' }
    });
    return response.data;
  },

  // Aliasy dla kompatybilności z komponentami TaskList
  startSession: async (taskId: number): Promise<ApiResponse<{ session: TimerSession }>> => {
    return timersAPI.startTimer({ task_id: taskId });
  },

  stopSession: async (sessionId: number): Promise<ApiResponse<{ session: TimerSession }>> => {
    return timersAPI.stopTimer(sessionId);
  },
};

// Utility functions
export const formatTime = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
  return `${minutes}:${secs.toString().padStart(2, '0')}`;
};

export const formatTimeFromMs = (milliseconds: number): string => {
  return formatTime(Math.floor(milliseconds / 1000));
};

export const getPriorityColor = (priority: 1 | 2 | 3): string => {
  switch (priority) {
    case 1: return '#4CAF50'; // Zielony - niski
    case 2: return '#FF9800'; // Pomarańczowy - średni  
    case 3: return '#F44336'; // Czerwony - wysoki
    default: return '#9E9E9E'; // Szary - domyślny
  }
};

export const getPriorityLabel = (priority: 1 | 2 | 3): string => {
  switch (priority) {
    case 1: return 'Niski';
    case 2: return 'Średni';
    case 3: return 'Wysoki';
    default: return 'Nieznany';
  }
};
